// src/shared/lib/practice-time.ts
//
// "Now" for the UI. On main every screen read the browser's clock (`new Date()`), and this file keeps exactly
// that behaviour: `practiceNow()` is the browser's now and the zone is the browser's zone.
//
// Follow-up (not in this PR): read "now" as the practice zone's wall clock (`organizationTimeZone` from
// POST /api/v1/Practice/calendarSlots, default Asia/Dubai). The exports below stay so callers already route
// through one seam; `setPracticeTimeZone` is accepted and ignored until that follow-up lands.
// This is NOT for what is sent to the server (headers, payloads).

function browserZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/** Accepted and ignored: the practice zone is a follow-up; the UI uses the browser's zone, as on main. */
export function setPracticeTimeZone(_zone: string | null | undefined): void {}

/** The zone the UI reads time in: the browser's IANA zone. */
export function practiceTimeZone(): string {
  return browserZone();
}

/** The instant (default: now) as a Date in the browser's zone, i.e. main's `new Date()`. */
export function practiceNow(instant: Date = new Date()): Date {
  return new Date(instant.getTime());
}

/** "Berlin · GMT+2", computed for the browser's zone (DST handled by Intl) at the given instant. */
export function practiceZoneLabel(date: Date = new Date()): string {
  const zone = browserZone();
  const city = (zone.split('/').pop() ?? zone).replace(/_/g, ' ');
  const raw = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
    .formatToParts(date)
    .find((p) => p.type === 'timeZoneName')?.value;
  const offset = !raw || raw === 'GMT' ? 'GMT+0' : raw.replace('−', '-');
  return `${city} · ${offset}`;
}

/** True when `d` (a Date, or an ISO string read as its digits) is on the browser's today. */
export function isPracticeToday(d: Date | string): boolean {
  const date = typeof d === 'string' ? new Date(d.replace(/(Z|[+-]\d{2}:?\d{2})$/i, '')) : d;
  if (Number.isNaN(date.getTime())) return false;
  const n = practiceNow();
  return date.getFullYear() === n.getFullYear() && date.getMonth() === n.getMonth() && date.getDate() === n.getDate();
}
